import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, DollarSign, Calendar } from 'lucide-react';
import styles from './DetailModal.module.css';
import { getCaseDetails } from '../../../services/clienteService';
import { EstadoCaso, FullCaseDetails } from '../../../interfaces/cliente.types';
import { Audiencia } from '../../../interfaces/audiencia.types';
import { Pago } from '../../../interfaces/pago.types';
import { CasoInfoCard } from './cardinfo/CasoInfoCard';
import { CasoFinancialCard } from './cardinfo/CasoFinancialCard';
import { CasoAgendaCard } from './cardinfo/CasoAgendaCard';
import { AddPagoModal } from './cardinfo/modales/AddPagoModal';
import { AddAudienciaModal } from './cardinfo/modales/AddAudienciaModal';

export const CasoDetailModal: React.FC<{ onClose: () => void; caseId: number; }> = ({ onClose, caseId }) => {
    const [caso, setCaso] = useState<FullCaseDetails | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'info' | 'financiero' | 'agenda'>('info');

    const [isAddPagoModalOpen, setIsAddPagoModalOpen] = useState(false);
    const [isAddAudienciaModalOpen, setIsAddAudienciaModalOpen] = useState(false);



    useEffect(() => {
        if (!caseId) return;
        const fetchDetails = async () => {
            setIsLoading(true);
            try {
                const data = await getCaseDetails(caseId);
                setCaso(data);
            } catch (err: any) { setError(err.message); }
            finally { setIsLoading(false); }
        };
        fetchDetails();
    }, [caseId]);

    const handlePagoAdded = (newPago: Pago) => {
        if (caso) {
            const updatedCaso = {
                ...caso,
                pagos: [newPago, ...caso.pagos].sort((a, b) => new Date(b.fecha_pago).getTime() - new Date(a.fecha_pago).getTime()),
                saldo_pendiente: caso.saldo_pendiente - newPago.monto_pagado,
            };
            setCaso(updatedCaso);
        }
    };

    const handleAudienciaAdded = (newAudiencia: Audiencia) => {
        if (caso) {
            const updatedCaso = {
                ...caso,
                audiencias: [newAudiencia, ...caso.audiencias].sort((a, b) => new Date(a.fecha_hora).getTime() - new Date(b.fecha_hora).getTime()),
            };
            setCaso(updatedCaso);
        }
    };

    const tabs = [
        { id: 'info', label: 'Información', icon: User },
        { id: 'financiero', label: 'Finanzas', icon: DollarSign },
        { id: 'agenda', label: 'Agenda', icon: Calendar },
    ];

    return (
        <>
            <div className={styles.modalOverlay} onClick={onClose}>
                <motion.div className={styles.modalContent} /* ... */ onClick={(e) => e.stopPropagation()}>
                    <header className={styles.modalHeader}>
                        <div>
                            <span className={styles.headerTag}>Detalles del Caso</span>
                            <h2>{caso?.tipo_caso || 'Cargando...'}</h2>
                            <p>{caso?.nombre_completo}</p>
                        </div>
                        <button onClick={onClose} className={styles.closeButton}><X size={24} /></button>
                    </header>
                    <div className={styles.tabNav}>
                        {tabs.map(tab => (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`${styles.tabButton} ${activeTab === tab.id ? styles.activeTab : ''}`}>
                                <tab.icon size={18} /><span>{tab.label}</span>
                                {activeTab === tab.id && <motion.div className={styles.underline} layoutId="underline" />}
                            </button>
                        ))}
                    </div>
                    <main className={styles.tabContent}>
                        <AnimatePresence mode="wait">
                            {isLoading ? <p>Cargando...</p> : error ? <p>{error}</p> : caso && (
                                <>
                                    {activeTab === 'info' && <CasoInfoCard key="info" caso={caso} />}
                                    {activeTab === 'financiero' && <CasoFinancialCard key="financiero" caso={caso} onAddPago={() => setIsAddPagoModalOpen(true)} />}
                                    {activeTab === 'agenda' && <CasoAgendaCard
                                        key="agenda"
                                        audiencias={caso.audiencias}
                                        onAddAudiencia={() => setIsAddAudienciaModalOpen(true)}
                                    />}
                                </>
                            )}
                        </AnimatePresence>
                    </main>
                </motion.div>
            </div>
            <AnimatePresence>
                {isAddPagoModalOpen && caso && <AddPagoModal onClose={() => setIsAddPagoModalOpen(false)} onPagoAdded={handlePagoAdded} caseId={caso.id} />}
                {isAddAudienciaModalOpen && caso && <AddAudienciaModal onClose={() => setIsAddAudienciaModalOpen(false)} onAudienciaAdded={handleAudienciaAdded} caseId={caso.id} />}
            </AnimatePresence>
        </>
    );
};